export interface ActivatePaidActionParams {
    isCurrentPlan: boolean;
    isCurrentCycle: boolean;
    canActivatePaidNow: boolean;
    isDeferredChange?: boolean;
}

export type TrialPlanCode =
    | 'SOLO_199'
    | 'SOLO_399'
    | 'BASIC'
    | 'SOLO_999'
    | 'SOLO_1299'
    | 'TEAM_1499'
    | 'TEAM_2999'
    | 'PRO'
    | 'TEAM_9999'
    | 'TEAM_49999';
export type TrialBillingCycle = 'monthly' | 'yearly';

export interface TrialPlanCardActionParams {
    currentPlanCode: TrialPlanCode;
    currentCycle: TrialBillingCycle;
    selectedPlanCode: TrialPlanCode;
    selectedCycle: TrialBillingCycle;
    planCode: TrialPlanCode;
    cycle: TrialBillingCycle;
    requiresCheckout?: boolean;
}

export interface TrialStartSubscriptionCardStateParams {
    requiresCheckout: boolean;
    isPurchasable: boolean;
}

export interface TrialStartSubscriptionCardState {
    actionLabel: 'Start subscription';
    isDisabled: boolean;
}

export const DIRECT_SUBSCRIPTION_PAYMENT_SUCCESS_EVENT = 'billing:direct-subscription-success';

export interface DirectSubscriptionPaymentSuccessDetail {
    kind: 'upgrade';
    workspaceId?: string;
    planCode?: string | null;
    planName?: string | null;
    billingCycle?: TrialBillingCycle;
}

export const buildDirectSubscriptionPaymentSuccessDetail = ({
    workspaceId,
    planCode,
    planName,
    billingCycle,
}: {
    workspaceId?: string;
    planCode?: string | null;
    planName?: string | null;
    billingCycle?: TrialBillingCycle;
}): DirectSubscriptionPaymentSuccessDetail => ({
    kind: 'upgrade',
    workspaceId,
    planCode,
    planName,
    billingCycle,
});

export type TrialActivationCheckoutResult =
    | { kind: 'redirect'; paymentUrl: string }
    | { kind: 'completed' }
    | { kind: 'failed' };

export const resolveTrialActivationCheckoutResult = (
    response?: { success?: boolean; paymentUrl?: string | null } | null,
): TrialActivationCheckoutResult => {
    if (!response?.success) {
        return { kind: 'failed' };
    }

    if (typeof response.paymentUrl === 'string' && response.paymentUrl.trim().length > 0) {
        return { kind: 'redirect', paymentUrl: response.paymentUrl };
    }

    return { kind: 'completed' };
};

export const resolveTrialWorkspaceState = ({
    setupStatusIsInTrial,
    billingStateIsTrial,
}: {
    setupStatusIsInTrial: boolean;
    billingStateIsTrial: boolean;
}): boolean => {
    return Boolean(setupStatusIsInTrial || billingStateIsTrial);
};

export const shouldShowActivatePaidAction = ({
    isCurrentPlan,
    isCurrentCycle,
    canActivatePaidNow,
    isDeferredChange = false,
}: ActivatePaidActionParams): boolean => {
    return Boolean(isCurrentPlan && isCurrentCycle && canActivatePaidNow && !isDeferredChange);
};

export const shouldRequireCheckoutForTrialPlanSelection = ({
    isInTrial,
    hasSetupCard,
}: {
    isInTrial: boolean;
    hasSetupCard: boolean;
}): boolean => {
    return Boolean(isInTrial && !hasSetupCard);
};

export const getTrialStartSubscriptionCardState = ({
    requiresCheckout,
    isPurchasable,
}: TrialStartSubscriptionCardStateParams): TrialStartSubscriptionCardState | null => {
    if (!requiresCheckout) {
        return null;
    }

    return {
        actionLabel: 'Start subscription',
        isDisabled: !isPurchasable,
    };
};

const PLAN_RANK: Record<TrialPlanCode, number> = {
    SOLO_199: 10,
    SOLO_399: 11,
    BASIC: 12,
    SOLO_999: 13,
    SOLO_1299: 14,
    TEAM_1499: 20,
    TEAM_2999: 30,
    PRO: 40,
    TEAM_9999: 50,
    TEAM_49999: 60,
};

const getPlanRank = (planCode: TrialPlanCode): number => {
    return PLAN_RANK[planCode] ?? 0;
};

export const isImmediateTrialActivationSelection = ({
    currentPlanCode,
    currentCycle,
    selectedPlanCode,
    selectedCycle,
}: Pick<TrialPlanCardActionParams, 'currentPlanCode' | 'currentCycle' | 'selectedPlanCode' | 'selectedCycle'>): boolean => {
    const isCurrentTrialPlan = currentPlanCode === selectedPlanCode && currentCycle === selectedCycle;
    const isSameCycleUpgrade = currentCycle === selectedCycle
        && getPlanRank(selectedPlanCode) > getPlanRank(currentPlanCode);

    return isCurrentTrialPlan || isSameCycleUpgrade;
};

export const getTrialPlanCardAction = ({
    currentPlanCode,
    currentCycle,
    selectedPlanCode,
    selectedCycle,
    planCode,
    cycle,
    requiresCheckout = false,
}: TrialPlanCardActionParams): string => {
    if (requiresCheckout) {
        return 'Start subscription';
    }

    const isSelectedPlan = selectedPlanCode === planCode;
    const isSelectedCycle = selectedCycle === cycle;
    const isImmediateSelection = isImmediateTrialActivationSelection({
        currentPlanCode,
        currentCycle,
        selectedPlanCode,
        selectedCycle,
    });

    if (isSelectedPlan && isSelectedCycle && isImmediateSelection) {
        return 'Activate Paid Plan';
    }

    if (isSelectedPlan && isSelectedCycle && currentPlanCode === selectedPlanCode && currentCycle !== selectedCycle) {
        return cycle === 'yearly' ? 'Switch to Yearly' : 'Switch to Monthly';
    }

    if (isSelectedPlan && currentPlanCode === selectedPlanCode && !isSelectedCycle) {
        return cycle === 'yearly' ? 'Switch to Yearly' : 'Switch to Monthly';
    }

    if (planCode.startsWith('SOLO_') || planCode === 'BASIC') {
        return 'Switch to Solo';
    }

    return 'Switch to Team';
};
